import { validateField, validateForm, rerenderValidationNew } from "../utils/validation";
import { CPR_REQUEST_VALIDATION_RULES } from "../validation/apiCpraRequestValidationRules";
import { showToast } from "../_components/toast";
import { trans } from "../i18n/i18n";
import { submitCpraRequest } from "../api/cpra";
import { Toast } from "../consts/toast";
import { trackEvent } from "../tracking/gtm";

const CPR_REQUEST_WHITELIST = Object.fromEntries(
    Object.keys(CPR_REQUEST_VALIDATION_RULES)
        .map(key => [key, true])
);

document.addEventListener("DOMContentLoaded", function () {
    const form = document.getElementById("cpra-request-form");
    if (!form) return;

    form.addEventListener("input", handleCpraFormInput);
    form.addEventListener("change", handleCpraFormInput);
    form.addEventListener("submit", submitCpraRequestForm);
});

// Clears validation state and feedback for a checkbox input without adding any new state.
// Specifically removes is-valid/is-invalid classes and any sibling feedback injected previously.
function clearCheckboxValidation(inputEl) {
    if (!(inputEl instanceof HTMLElement)) return;
    if (inputEl.type !== 'checkbox') return;

    inputEl.classList.remove("is-valid", "is-invalid");

    const parent = inputEl.closest('.form-check') || inputEl.parentElement;
    if (!parent) return;

    Array.from(parent.querySelectorAll('.invalid-feedback, .valid-feedback')).forEach(el => el.remove());
    if (parent.nextElementSibling && (parent.nextElementSibling.classList.contains('invalid-feedback') || parent.nextElementSibling.classList.contains('valid-feedback'))) {
        parent.nextElementSibling.remove();
    }
}

// Handles validation on individual form fields when they are changed or updated.
function handleCpraFormInput(e) {
    const inputEl = e.target;
    const form = inputEl.closest("form");

    // Special handling for array field rights[] -> rule key is "rights"
    if (inputEl.name === "rights[]") {
        // Determine rights[] validity manually: at least one checked
        const anyChecked = form.querySelectorAll('input[name="rights[]"]:checked').length > 0;
        const msgs = anyChecked ? [] : [trans('validation.required')];
        // Ensure no per-checkbox feedback is shown; clear any existing feedback from previous runs
        const rightsCheckboxes = form.querySelectorAll('input[name="rights[]"]');
        rightsCheckboxes.forEach(clearCheckboxValidation);

        // Render group error into #rights-error div if present
        const rightsErrorDiv = form.querySelector('#rights-error');
        if (rightsErrorDiv) {
            rightsErrorDiv.textContent = msgs[0] || "";
            rightsErrorDiv.classList.toggle('d-none', msgs.length === 0);
        }
        return;
    }

    // Validate the field and render errors
    const allErrors = validateField(form, inputEl, CPR_REQUEST_VALIDATION_RULES, CPR_REQUEST_WHITELIST);
    const fieldErrors = allErrors?.[inputEl.name] || [];
    rerenderValidationNew(inputEl, fieldErrors);
}

// Disables or enables all form fields inside the form
function setFormDisabledState(form, isDisabled) {
    const elements = form.querySelectorAll("input, select, textarea, button");
    elements.forEach((el) => {
        el.disabled = isDisabled;
    });
}

// Handles form submission: validates the entire form, sends data via API,
// and displays success or error messages.
async function submitCpraRequestForm(e) {
    e.preventDefault();
    const form = e.target;

    let hasErrors = validateForm(form, CPR_REQUEST_VALIDATION_RULES, CPR_REQUEST_WHITELIST);
    // Determine rights[] validity manually
    const anyChecked = form.querySelectorAll('input[name="rights[]"]:checked').length > 0;
    if (anyChecked && hasErrors && hasErrors.rights) {
        delete hasErrors.rights;
        if (Object.keys(hasErrors).length === 0) {
            hasErrors = undefined;
        }
    }

    if (hasErrors) {
        const message = trans("Please correct the errors in the form before submitting.");
        showToast(message, Toast.ERROR);

        // Ensure rights[] group error shows up in #rights-error
        const rightsMsgs = anyChecked ? [] : [trans('validation.required')];
        const rightsErrorDiv = form.querySelector('#rights-error');
        if (rightsErrorDiv) {
            rightsErrorDiv.textContent = rightsMsgs[0] || "";
            rightsErrorDiv.classList.toggle('d-none', rightsMsgs.length === 0);
        }
        // Also make sure no per-checkbox feedback is rendered for rights[]
        const rightsCheckboxes = form.querySelectorAll('input[name="rights[]"]');
        rightsCheckboxes.forEach(clearCheckboxValidation);
        return;
    }

    const formData = new FormData(form);

    // Disable while submitting
    setFormDisabledState(form, true);

    try {
        await submitCpraRequest(formData);

        showToast(trans("Your privacy request has been submitted successfully."), Toast.SUCCESS);
        trackEvent("form_cpra_request", { rights: formData.getAll("rights[]") });

        form.reset();
        resetCpraValidation(form);
    } catch (error) {
        const errors = error?.response?.data?.errors;
        if (errors) {
            Object.entries(errors).forEach(([field, msgs]) => {
                // For array field (rights), map to first checkbox element
                let input;
                if (field === "rights") {
                    const rightsErrorDiv = form.querySelector('#rights-error');
                    if (rightsErrorDiv) {
                        rightsErrorDiv.textContent = (Array.isArray(msgs) ? msgs : [msgs])[0] || "";
                        rightsErrorDiv.classList.toggle('d-none', !msgs || (Array.isArray(msgs) && msgs.length === 0));
                    }
                    // Clear any per-checkbox feedback so the error shows only in #rights-error
                    const rightsCheckboxes = form.querySelectorAll('input[name="rights[]"]');
                    rightsCheckboxes.forEach(clearCheckboxValidation);
                } else {
                    input = document.getElementById(field) || form.querySelector(`[name="${field}"]`);
                }
                if (input) rerenderValidationNew(input, Array.isArray(msgs) ? msgs : [msgs]);
            });
        }
    } finally {
		setFormDisabledState(form, false);

		// Keep submit button(s) disabled for an extra 1s cooldown to prevent double submits
		const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"]');
		submitButtons.forEach(btn => { btn.disabled = true; });
		setTimeout(() => {
			submitButtons.forEach(btn => { btn.disabled = false; });
		}, 1000);
    }
}

function resetCpraValidation(form) {
	form.querySelectorAll('input, select, textarea').forEach(inputEl => {
		inputEl.classList.remove('is-valid', 'is-invalid');

		const isCheckbox = inputEl.type === 'checkbox';
		const parent = isCheckbox
			? (inputEl.closest('.form-check') || inputEl.parentElement)
			: (inputEl.closest('.form-floating') || inputEl.parentElement);

		if (parent) {
			Array.from(parent.querySelectorAll('.invalid-feedback, .valid-feedback')).forEach(el => el.remove());
			if (isCheckbox && parent.nextElementSibling && (parent.nextElementSibling.classList.contains('invalid-feedback') || parent.nextElementSibling.classList.contains('valid-feedback'))) {
				parent.nextElementSibling.remove();
			}
		}
	});

	const rightsErrorDiv = form.querySelector('#rights-error');
	if (rightsErrorDiv) {
		rightsErrorDiv.textContent = '';
		rightsErrorDiv.classList.add('d-none');
	}
}
